"use client";

import { c as _c } from "react/compiler-runtime";
import { api } from "@agroproj/backend/convex/_generated/api";
import type { Id } from "@agroproj/backend/convex/_generated/dataModel";
import { FEATURE_FLAG_KEYS, isAssistantClientEnabled } from "@agroproj/backend/convex/assistant/config";
import { getAssistantRouteContext } from "@agroproj/backend/convex/assistant/routeContext";
import { useMutation, useQuery } from "convex/react";
import { ArrowDownIcon, ArrowUpIcon, BookmarkIcon, BotIcon, ChevronLeftIcon, SquareIcon, SquarePenIcon } from "lucide-react";
import { usePathname } from "next/navigation";
import { createContext, type Dispatch, type FormEvent, type KeyboardEvent, type ReactElement, type ReactNode, type SetStateAction, use, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useStickToBottom } from "use-stick-to-bottom";
import { z } from "zod";
import { type AssistantFeedback, AssistantMessage, hasVisibleContent } from "@/components/assistant/assistant-message";
import { AssistantSuggestions } from "@/components/assistant/assistant-suggestions";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupTextarea } from "@/components/ui/input-group";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { metaTextClassName } from "@/components/ui/typography";
import { useAssistantChat } from "@/hooks/use-assistant-chat";
import { useConvexAuthReady } from "@/hooks/use-convex-auth-ready";
import { useConvexFeatureFlag } from "@/hooks/use-convex-feature-flag";
import { getAssistantDomainDescription, getAssistantPlaceholder } from "@/lib/assistant/suggestions";
import { dateFormat } from "@/lib/date-format";
import { capturePosthogEvent } from "@/lib/posthog";
import { cn } from "@/lib/utils";
import { AssistantDocuments, type DocumentChatContext } from "./assistant-documents";
import { AssistantHistory } from "./assistant-history";
const errorBody = z.object({
  error: z.string().min(1)
});

/** The route returns `{ error }` JSON in Portuguese; the transport surfaces it as the message. */
function getErrorMessage(error: Error) {
  try {
    const parsed = errorBody.safeParse(JSON.parse(error.message));
    if (parsed.success) return parsed.data.error;
  } catch {
    // Network failures and stream errors carry plain text.
  }
  return "O assistente não conseguiu responder agora.";
}

/** Icon button with a tooltip, for header actions that nothing else names. */
function HeaderAction(t0) {
  const $ = _c(7);
  const {
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <TooltipTrigger render={children} />;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <TooltipContent side="bottom">{label}</TooltipContent>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <Tooltip>{t1}{t2}</Tooltip>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
type AssistantPanelState = {
  isOpen: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
};

/** Null when the assistant is off, outside the app shell, or on admin routes. */
const AssistantPanelContext = createContext<AssistantPanelState | null>(null);

/**
 * Mounts the assistant sheet once for the app shell, so the conversation survives navigation.
 * Page headers open it through `AssistantHeaderTrigger`.
 */
export function AssistantPanelProvider(t0) {
  const $ = _c(14);
  const {
    children
  } = t0;
  const {
    enabled: convexEnabled,
    isLoading
  } = useConvexFeatureFlag(FEATURE_FLAG_KEYS.ASSISTANT);
  const [lastResolvedEnabled, setLastResolvedEnabled] = useState(false);
  const [isOpen, setOpen] = useState(false);
  if (!isLoading && lastResolvedEnabled !== convexEnabled) {
    setLastResolvedEnabled(convexEnabled);
  }
  let t1;
  if ($[0] !== convexEnabled || $[1] !== isLoading || $[2] !== lastResolvedEnabled) {
    t1 = isAssistantClientEnabled() && (isLoading ? lastResolvedEnabled : convexEnabled);
    $[0] = convexEnabled;
    $[1] = isLoading;
    $[2] = lastResolvedEnabled;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const shouldRender = t1;
  let t2;
  if ($[4] !== isOpen || $[5] !== shouldRender) {
    t2 = shouldRender ? {
      isOpen,
      setOpen
    } : null;
    $[4] = isOpen;
    $[5] = shouldRender;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== isOpen || $[8] !== shouldRender) {
    t3 = shouldRender ? <AssistantPanelContent isOpen={isOpen} setOpen={setOpen} /> : null;
    $[7] = isOpen;
    $[8] = shouldRender;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== children || $[11] !== t2 || $[12] !== t3) {
    t4 = <AssistantPanelContext value={t2}>{children}{t3}</AssistantPanelContext>;
    $[10] = children;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}

/** Header button that opens the assistant. Renders nothing where the assistant is unavailable. */
export function AssistantHeaderTrigger() {
  const $ = _c(11);
  const panel = use(AssistantPanelContext);
  if (!panel) {
    return null;
  }
  let t0;
  if ($[0] !== panel) {
    t0 = () => panel.setOpen(true);
    $[0] = panel;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== panel.isOpen || $[3] !== t0) {
    t1 = <Button aria-expanded={panel.isOpen} aria-keyshortcuts="Meta+J Control+J" aria-label="Abrir assistente" onClick={t0} size="icon-sm" variant="outline" />;
    $[2] = panel.isOpen;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <BotIcon />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1) {
    t3 = <TooltipTrigger render={t1}>{t2}</TooltipTrigger>;
    $[6] = t1;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <TooltipContent side="bottom">Assistente (⌘J ou Ctrl+J)</TooltipContent>;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3) {
    t5 = <Tooltip>{t3}{t4}</Tooltip>;
    $[9] = t3;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
function AssistantPanelContent({
  isOpen,
  setOpen
}: AssistantPanelState) {
  const pathname = usePathname();
  const routeContext = useMemo(() => getAssistantRouteContext(pathname), [pathname]);
  const placeholder = useMemo(() => getAssistantPlaceholder(routeContext), [routeContext]);
  const domainDescription = useMemo(() => getAssistantDomainDescription(routeContext), [routeContext]);
  // Wait for Convex auth: these run above the org page, so an auth error here would crash
  // the whole app instead of showing `EnforceOrganization`'s loading screen.
  const {
    canQuery
  } = useConvexAuthReady();
  const historyAvailable = useQuery(api.assistant.history.available, canQuery ? {} : "skip");
  const createThread = useMutation(api.assistant.history.create);
  const [threadId, setThreadId] = useState<Id<"assistant_threads">>();
  const thread = useQuery(api.assistant.history.read, canQuery && threadId ? {
    threadId
  } : "skip");
  const [documentContext, setDocumentContext] = useState<DocumentChatContext>({
    blocked: false
  });
  const [documentTurn, setDocumentTurn] = useState(0);
  const {
    messages,
    sendMessage,
    status,
    error,
    setMessages,
    stop,
    regenerate
  } = useAssistantChat(routeContext, threadId ? {
    threadId,
    revision: thread?.revision ?? 0
  } : undefined, documentContext.request);
  const loadedThread = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (threadId && thread && status !== "submitted" && status !== "streaming" && loadedThread.current !== `${threadId}:${thread.revision}:${thread.turns.map(turn_1 => turn_1.unavailable).join()}`) {
      setMessages(thread.turns.flatMap(turn => [{
        id: `${turn.id}-u`,
        role: "user" as const,
        parts: [{
          type: "text" as const,
          text: turn.userText
        }]
      }, {
        id: `${turn.id}-a`,
        role: "assistant" as const,
        parts: [{
          type: "text" as const,
          text: turn.assistantText
        }],
        metadata: {
          proposalIds: turn.proposalIds,
          producerProposalIds: turn.producerProposalIds,
          recordProposalIds: turn.recordProposalIds
        }
      }]));
      loadedThread.current = `${threadId}:${thread.revision}:${thread.turns.map(turn_0 => turn_0.unavailable).join()}`;
    }
  }, [threadId, thread, status, setMessages]);
  const previousOrganization = useRef(routeContext.organizationSlug);
  useEffect(() => {
    if (previousOrganization.current === routeContext.organizationSlug) return;
    previousOrganization.current = routeContext.organizationSlug;
    void stop();
    setThreadId(undefined);
    setMessages([]);
    loadedThread.current = undefined;
  }, [routeContext.organizationSlug, stop, setMessages]);
  useEffect(() => {
    if (threadId && thread === null) {
      void stop();
      setMessages([]);
    }
  }, [threadId, thread, stop, setMessages]);
  useEffect(() => {
    if (historyAvailable === false && threadId) {
      void stop();
      setThreadId(undefined);
      setMessages([]);
      loadedThread.current = undefined;
    }
  }, [historyAvailable, threadId, stop, setMessages]);
  const [input, setInput] = useState("");
  const [feedbackByMessage, setFeedbackByMessage] = useState<Record<string, AssistantFeedback>>({});
  const [view, setView] = useState<"chat" | "history">("chat");
  const [creatingThread, setCreatingThread] = useState(false);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const {
    scrollRef,
    contentRef,
    isAtBottom,
    scrollToBottom
  } = useStickToBottom({
    initial: "instant",
    resize: "instant"
  });
  const isBusy = status === "submitted" || status === "streaming";
  const isLoadingThread = Boolean(threadId && thread === undefined);
  const blockedHistory = Boolean(threadId && (!thread || thread.archived || thread.busy || thread.turns.some(turn_2 => turn_2.unavailable)));
  // A saved turn is temporarily unavailable while its response is being persisted.
  const showHistoryWarning = blockedHistory && !isBusy && !thread?.busy;
  const canSend = input.trim().length > 0 && !isBusy && !blockedHistory && !documentContext.blocked;
  const lastMessage = messages.at(-1);
  const isThinking = isBusy && !(lastMessage?.role === "assistant" && hasVisibleContent(lastMessage));
  const isEmpty = messages.length === 0;

  // ⌘J / Ctrl+J toggles the panel from anywhere in the app.
  useEffect(() => {
    function onKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key.toLowerCase() !== "j" || !(event.metaKey || event.ctrlKey)) return;
      if (event.altKey || event.shiftKey) return;
      event.preventDefault();
      setOpen(open => !open);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [setOpen]);

  // Returning from the history view puts the cursor back in the composer.
  useEffect(() => {
    if (view === "chat") composerRef.current?.focus();
  }, [view]);
  function selectThread(id?: Id<"assistant_threads">) {
    setView("chat");
    // Reopening the current thread keeps its messages; the load effect only runs on change.
    if (id !== undefined && id === threadId) return;
    void stop();
    setMessages([]);
    setThreadId(id);
    loadedThread.current = undefined;
  }
  async function toggleSaved() {
    if (threadId) {
      selectThread(undefined);
      return;
    }
    if (!routeContext.organizationSlug) return;
    setCreatingThread(true);
    try {
      selectThread(await createThread({
        organizationSlug: routeContext.organizationSlug
      }));
    } catch {
      toast.error("Não foi possível salvar a conversa.");
    } finally {
      setCreatingThread(false);
    }
  }
  async function submitPrompt(prompt: string) {
    const trimmed = prompt.trim();
    if (!trimmed || isBusy || blockedHistory || documentContext.blocked) return;
    setInput("");
    void scrollToBottom();
    await sendMessage({
      text: trimmed
    });
    setDocumentContext({
      blocked: false
    });
    setDocumentTurn(current => current + 1);
  }
  async function handleSubmit(event_0: FormEvent<HTMLFormElement>) {
    event_0.preventDefault();
    await submitPrompt(input);
  }
  function handleComposerKeyDown(event_1: KeyboardEvent<HTMLTextAreaElement>) {
    // Enter sends and Shift+Enter adds a line; IME composition keeps Enter for the candidate.
    if (event_1.key !== "Enter" || event_1.shiftKey || event_1.nativeEvent.isComposing) return;
    event_1.preventDefault();
    void submitPrompt(input);
  }
  function submitFeedback(messageId: string, rating: AssistantFeedback) {
    setFeedbackByMessage(current_0 => ({
      ...current_0,
      [messageId]: rating
    }));
    capturePosthogEvent("AgroProj:Assistant:FeedbackSubmitted", {
      rating,
      domain: routeContext.domain,
      recordType: routeContext.recordType ?? "none"
    });
  }
  const closePanel = useCallback(() => setOpen(false), [setOpen]);
  return <Sheet open={isOpen} onOpenChange={setOpen}>
      <SheetContent className="flex size-full flex-col gap-0 p-0 data-[side=right]:w-full data-[side=right]:sm:max-w-md" initialFocus={composerRef}
    // Light dim without blur so the page stays readable beside the chat.
    overlayClassName="bg-black/30 supports-backdrop-filter:[backdrop-filter:none]" showCloseButton side="right">
        <SheetHeader className="shrink-0 flex-row items-center gap-1 border-b py-4 pr-13 pl-4">
          {historyAvailable && view === "chat" ? <HeaderAction label="Conversas">
              <Button aria-label="Ver conversas" className="-ml-2" onClick={() => setView("history")} size="icon-sm" variant="ghost">
                <ChevronLeftIcon />
              </Button>
            </HeaderAction> : null}
          <SheetTitle className="min-w-0 flex-1 truncate">
            {view === "history" ? "Conversas" : "Assistente"}
          </SheetTitle>
          <SheetDescription className="sr-only">{domainDescription}</SheetDescription>
          <HeaderAction label="Nova conversa">
            <Button aria-label="Nova conversa" disabled={isBusy || view === "chat" && !threadId && isEmpty} onClick={() => selectThread(undefined)} size="icon-sm" variant="ghost">
              <SquarePenIcon />
            </Button>
          </HeaderAction>
        </SheetHeader>

        {view === "history" && historyAvailable ? <AssistantHistory busy={isBusy} onResumeTemporary={!threadId && !isEmpty ? () => setView("chat") : undefined} onSelect={selectThread} selected={threadId} /> : <div className="flex min-h-0 flex-1 flex-col">
            <div className="relative min-h-0 flex-1">
              <div className="h-full overflow-y-auto overscroll-contain" ref={scrollRef}>
                <div className="flex flex-col gap-5 p-4" ref={contentRef}>
                  {isLoadingThread ? <p className={metaTextClassName}>Carregando conversa...</p> : isEmpty ? <AssistantSuggestions context={routeContext} onPrompt={submitPrompt} /> : messages.map(message => <AssistantMessage feedback={feedbackByMessage[message.id]} isStreaming={isBusy && message.id === lastMessage?.id} key={message.id} message={message} onFeedback={rating_0 => submitFeedback(message.id, rating_0)} onNavigate={closePanel} />)}
                  {isThinking ? <output aria-live="polite" className={metaTextClassName}>
                      Pensando...
                    </output> : null}
                  {error ? <Alert variant="destructive">
                      <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
                        {getErrorMessage(error)}
                        {!blockedHistory ? <Button onClick={() => void regenerate()} size="sm" variant="outline">
                            Tentar novamente
                          </Button> : null}
                      </AlertDescription>
                    </Alert> : null}
                </div>
              </div>
              {!isAtBottom ? <Button aria-label="Ir para a última mensagem" className="absolute bottom-3 left-1/2 -translate-x-1/2 shadow-lg" onClick={() => void scrollToBottom()} size="icon-sm" variant="secondary">
                  <ArrowDownIcon />
                </Button> : null}
            </div>

            <form className="shrink-0 px-3 pt-1 pb-[max(0.75rem,env(safe-area-inset-bottom))]" onSubmit={handleSubmit}>
              {threadId && thread && (showHistoryWarning || !isBusy && !thread.busy && thread.truncated) ? <div className={cn(metaTextClassName, "flex items-center gap-2 px-1 pb-2")}>
                  <span className="min-w-0 flex-1">
                    {showHistoryWarning ? "Esta conversa não pode continuar agora." : "Exibindo apenas os turnos mais recentes."}
                  </span>
                  {showHistoryWarning ? <Button onClick={() => selectThread(undefined)} size="sm" variant="outline">
                      Nova conversa
                    </Button> : null}
                </div> : null}
              <AssistantDocuments key={`${routeContext.pathname}:${threadId ?? "temporary"}:${documentTurn}`} organizationSlug={routeContext.organizationSlug ?? ""} currentProjectId={routeContext.recordType === "project" ? routeContext.recordId as Id<"projects"> : undefined} disabled={isBusy} onChange={setDocumentContext} />
              <InputGroup>
                <InputGroupTextarea aria-label="Mensagem para o assistente" className="max-h-40 min-h-11" disabled={showHistoryWarning && !isLoadingThread} enterKeyHint="send" onChange={event_2 => setInput(event_2.target.value)} onKeyDown={handleComposerKeyDown} placeholder={placeholder} ref={composerRef} rows={1} value={input} />
                <InputGroupAddon align="block-end">
                  {historyAvailable && isEmpty && !isLoadingThread ? <InputGroupButton aria-pressed={Boolean(threadId)} disabled={creatingThread || !routeContext.organizationSlug} onClick={() => void toggleSaved()} size="sm" variant={threadId ? "secondary" : "ghost"}>
                      <BookmarkIcon />
                      Salvar conversa
                    </InputGroupButton> : <span className="truncate">
                      {threadId && thread ? `Salva até ${dateFormat(thread.expiresAt)}` : historyAvailable ? "Conversa temporária" : null}
                    </span>}
                  {isBusy ? <InputGroupButton aria-label="Parar resposta" className="ml-auto" onClick={() => void stop()} size="icon-sm" variant="secondary">
                      <SquareIcon className="fill-current" />
                    </InputGroupButton> : <InputGroupButton aria-label="Enviar mensagem" className="ml-auto" disabled={!canSend} size="icon-sm" type="submit" variant="default">
                      <ArrowUpIcon />
                    </InputGroupButton>}
                </InputGroupAddon>
              </InputGroup>
              {threadId && isEmpty && !isLoadingThread ? <p className={cn(metaTextClassName, "px-1 pt-2")}>
                  Conversa salva por 30 dias desde a criação. Dados pessoais reconhecidos são
                  removidos da cópia salva; consultas por identificador podem exigir nova entrada.
                </p> : null}
            </form>
          </div>}
      </SheetContent>
    </Sheet>;
}